import {
  all,
  takeLatest,
  call,
  put,
  delay,
  takeEvery
} from 'redux-saga/effects';
import Axios, { AxiosResponse } from 'axios';
import {
  getEnquiryDetailsSuccess,
  getEnquiryDetailsFailure,
  updateEnquiryDetailsSuccess,
  updateEnquiryDetailsFailure,
  getReferralDetailsSuccess,
  getReferralDetailsFailure,
  setEnquiryReferralsDetails,
  downloadCombinedPDFSuccess,
  downloadCombinedPDFFailure,
  downloadCombinedZIPSuccess,
  downloadCombinedZIPFailure,
  downloadPartialZipSuccess,
  downloadPartialZipFailure,
  viewAttachmentSuccess,
  viewAttachmentFailure,
  downloadAttachmentSuccess,
  downloadAttachmentFailure,
  getEnquiryAutoRenewPropertiesSuccess,
  getEnquiryAutoRenewPropertiesFailure,
  editEnquiryAutoRenewSuccess,
  editEnquiryAutoRenewFailure,
  archiveEnquirySuccess,
  archiveEnquiryFailure,
  uploadAttachmentFailure,
  uploadAttachmentSuccess,
  deleteAttachmentFailure,
  deleteAttachmentSuccess,
  moveEnquiryTeamSuccess,
  moveEnquiryTeamFailure,
  updateAttachmentSuccess,
  updateAttachmentFailure,
  getEnquiryAutoRenewSeriesSuccess,
  getEnquiryAutoRenewSeriesFailure,
  addEventFailure,
  addEventSuccess
} from './actions';
import { DetailsPanelActionTypes } from './types';
import {
  getEnquiryDetails,
  updateEnquiryDetails,
  getReferralDetails,
  getAnonymousReferralDetails,
  downloadCombinedPDF,
  downloadCombinedPDFCheckReady,
  downloadCombinedZIP,
  downloadCombinedZIPCheckReady,
  downloadPartialZip,
  viewAttachment,
  viewAttachmentAnonymous,
  downloadCombinedPDFAnonymous,
  downloadCombinedPDFCheckReadyAnonymous,
  downloadCombinedZIPAnonymous,
  downloadCombinedZIPCheckReadyAnonymous,
  downloadPartialZipAnonymous,
  getEnquiryAutoRenewProperties,
  editEnquiryAutoRenew,
  archiveEnquiry,
  unArchiveEnquiry,
  createAttachmentPresignedUrl,
  deleteAttachment,
  moveEnquiryTeam,
  updateAttachment,
  getEnquiryAutoRenewSeries
} from 'services/enquiry.service';
import { getFileFromUrl, uploadFileToS3 } from 'services/file.service';
import { downloadFile } from 'utilities/helper.util';
import { addEvent } from '../../../services/event.service';

function* handleGetEnquiryDetails(request: any) {
  try {
    const response: AxiosResponse = yield getEnquiryDetails(request.payload);
    if (response) {
      yield put(getEnquiryDetailsSuccess(response.data));
      const assetOwners = response.data.Assetowner.map((assetOwner: any) => {
        return {
          ...assetOwner,
          isLoading: true
        };
      });
      yield put(setEnquiryReferralsDetails(assetOwners));
    } else {
      yield put(getEnquiryDetailsFailure('Unable to get enquiry details'));
    }
  } catch (err) {
    let errMsg = undefined;
    if (Axios.isAxiosError(err)) {
      errMsg =
        err.response?.status === 403
          ? 'Your account do not have access to view this enquiry.'
          : err.response?.data?.msg;
    }
    yield put(
      getEnquiryDetailsFailure(errMsg || 'Unable to get enquiry details')
    );
  }
}

function* handleGetEnquiryReferrals(request: any) {
  const { enquiryId, assetOwner, isAnonymousView, plToken, vhToken } =
    request.payload;
  let errMsg = 'Unable to get referral details';
  try {
    const response: AxiosResponse = yield isAnonymousView
      ? getAnonymousReferralDetails(enquiryId, assetOwner.id, plToken, vhToken)
      : getReferralDetails(enquiryId, assetOwner.id);
    if (response) {
      yield put(getReferralDetailsSuccess(response.data));
    } else {
      yield put(getReferralDetailsFailure({ ...assetOwner, errMsg }));
    }
  } catch (err) {
    if (Axios.isAxiosError(err)) {
      errMsg = err.response?.data?.msg;
    }
    yield put(getReferralDetailsFailure({ ...assetOwner, errMsg }));
  }
}

function* handleDownloadCombinedPDF(request: any) {
  if (request.payload.isAnonymousView) {
    yield handleDownloadCombinedPDFAnonymous(request);
  } else {
    yield handleDownloadCombinedPDFAuthenticated(request);
  }
}

function* handleDownloadCombinedPDFAuthenticated(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedPDF(request.payload);
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkPDFReadyRes = '';
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedPDFCheckReady(request.payload, response.data);
        checkPDFReadyRes = checkPDFReady.data;
        intervalCount += 1;
        performCheck = false;

        if (checkPDFReadyRes !== 'Accepted') {
          const downloadURL = `${process.env.REACT_APP_BASE_URL}/enquiries/${request.payload.enquiryId}/view-pdf/${response.data.slug}?uid=${response.data.uid}&key=${response.data.key}&download=true`;
          yield put(downloadCombinedPDFSuccess(downloadURL));
        }

        if (intervalCount > 200) {
          yield put(
            downloadCombinedPDFFailure(
              'Unable to get the combined PDF. Files too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkPDFReadyRes === 'Accepted');
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* handleDownloadCombinedPDFAnonymous(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedPDFAnonymous(
      request.payload
    );
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkPDFReadyRes = '';
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedPDFCheckReadyAnonymous(
            request.payload,
            response.data
          );
        checkPDFReadyRes = checkPDFReady.data;
        intervalCount += 1;
        performCheck = false;

        if (checkPDFReadyRes !== 'Accepted') {
          const vhQS = request.payload.vhToken
            ? `&vh=${request.payload.vhToken}`
            : '';
          const plQS = request.payload.plToken
            ? `&pl=${request.payload.plToken}`
            : '';
          const downloadURL = `${process.env.REACT_APP_BASE_URL}/enquiries/external/${request.payload.enquiryId}/view-pdf/${response.data.slug}?uid=${response.data.uid}&key=${response.data.key}&download=true${vhQS}${plQS}`;
          yield put(downloadCombinedPDFSuccess(downloadURL));
        }

        if (intervalCount > 100) {
          yield put(
            downloadCombinedPDFFailure(
              'Unable to get the combined PDF. Files too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkPDFReadyRes === 'Accepted');
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* handleDownloadCombinedZIP(request: any) {
  if (request.payload.isAnonymousView) {
    yield handleDownloadCombinedZIPAnonymous(request);
  } else {
    yield handleDownloadCombinedZIPAuthenticated(request);
  }
}

function* handleDownloadCombinedZIPAuthenticated(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedZIP(request.payload);
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkZIPReadyRes = 202;
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedZIPCheckReady(request.payload, response.data);
        checkZIPReadyRes = checkPDFReady.status;
        intervalCount += 1;
        performCheck = false;

        if (checkZIPReadyRes !== 202) {
          const disposition = checkPDFReady.headers['content-disposition'];
          const filename = decodeURI(disposition.match(/filename="(.*)"/)[1]);
          const payload = {
            blobData: checkPDFReady.data,
            fileName: filename
          };
          yield put(downloadCombinedZIPSuccess(payload));
        }

        if (intervalCount > 100) {
          yield put(
            downloadCombinedZIPFailure(
              'Unable to get the combined ZIP. Files too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkZIPReadyRes === 202);
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* handleDownloadCombinedZIPAnonymous(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedZIPAnonymous(
      request.payload
    );
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkZIPReadyRes = 202;
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedZIPCheckReadyAnonymous(
            request.payload,
            response.data
          );
        checkZIPReadyRes = checkPDFReady.status;
        intervalCount += 1;
        performCheck = false;

        if (checkZIPReadyRes !== 202) {
          const disposition = checkPDFReady.headers['content-disposition'];
          const filename = decodeURI(disposition.match(/filename="(.*)"/)[1]);
          const payload = {
            blobData: checkPDFReady.data,
            fileName: filename
          };
          yield put(downloadCombinedZIPSuccess(payload));
        }

        if (intervalCount > 100) {
          yield put(
            downloadCombinedZIPFailure(
              'Unable to get the combined ZIP. Files are too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkZIPReadyRes === 202);
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* handleDownloadPartialZip(request: any) {
  if (request.payload.isAnonymousView) {
    yield handleDownloadPartialZipAnonymous(request);
  } else {
    yield handleDownloadPartialZipAuthenticated(request);
  }
}

function* handleDownloadPartialZipAuthenticated(request: any) {
  try {
    const response: AxiosResponse = yield downloadPartialZip(request.payload);
    if (response) {
      const disposition = response.headers['content-disposition'];
      const filename = decodeURI(disposition.match(/filename="(.*)"/)[1]);
      const payload = {
        blobData: response.data,
        fileName: filename
      };
      yield put(downloadPartialZipSuccess(payload));
    } else {
      yield put(downloadPartialZipFailure('Unable to download zip file'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadPartialZipFailure(errMsg || 'Unable to download zip file')
    );
  }
}

function* handleDownloadPartialZipAnonymous(request: any) {
  try {
    const response: AxiosResponse = yield downloadPartialZipAnonymous(
      request.payload
    );
    if (response) {
      const disposition = response.headers['content-disposition'];
      const filename = decodeURI(disposition.match(/filename="(.*)"/)[1]);
      const payload = {
        blobData: response.data,
        fileName: filename
      };
      yield put(downloadPartialZipSuccess(payload));
    } else {
      yield put(downloadPartialZipFailure('Unable to download zip file'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadPartialZipFailure(errMsg || 'Unable to download zip file')
    );
  }
}

function* handleViewAttachment(request: any) {
  try {
    let response: AxiosResponse;
    if (request.payload.isAnonymousView) {
      response = yield viewAttachmentAnonymous(request.payload);
    } else {
      response = yield viewAttachment(request.payload);
    }
    if (response?.data) {
      if (request.payload.download) {
        const file: AxiosResponse = yield getFileFromUrl(response.data.url);
        downloadFile(
          file.data,
          response.data.filename,
          response.data.content_type
        );
        yield put(viewAttachmentSuccess(undefined));
      } else {
        yield put(viewAttachmentSuccess(response.data));
      }
    } else {
      yield put(viewAttachmentFailure('Unable to view attachment'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(viewAttachmentFailure(errMsg || 'Unable to view attachment'));
  }
}

function* handleDownAttachment(request: any) {
  try {
    const file: AxiosResponse = yield getFileFromUrl(request.payload.url);
    downloadFile(
      file.data,
      request.payload.fileName,
      request.payload.contentType
    );
    yield put(downloadAttachmentSuccess());
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(downloadAttachmentFailure(errMsg || 'Unable to view attachment'));
  }
}

function* handleGetEnquiryAutoRenewProperties(request: any) {
  try {
    const response: AxiosResponse = yield getEnquiryAutoRenewProperties(
      request.payload
    );
    if (response) {
      yield put(getEnquiryAutoRenewPropertiesSuccess(response.data));
    } else {
      yield put(
        getEnquiryAutoRenewPropertiesFailure(
          'Unable to get enquiry auto renew properties'
        )
      );
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      getEnquiryAutoRenewPropertiesFailure(
        errMsg || 'Unable to get enquiry auto renew properties'
      )
    );
  }
}

function* handleEditAutoRenew(request: any) {
  try {
    const response: AxiosResponse = yield editEnquiryAutoRenew(request.payload);
    if (response) {
      const details: AxiosResponse = yield getEnquiryDetails(request.payload);
      if (details?.data) {
        yield put(updateEnquiryDetailsSuccess(details.data));
      }
      yield put(editEnquiryAutoRenewSuccess(response.data));
    } else {
      yield put(
        editEnquiryAutoRenewFailure('Unable to edit enquiry auto renew')
      );
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      editEnquiryAutoRenewFailure(errMsg || 'Unable to edit enquiry auto renew')
    );
  }
}

function* handleArchiveEnquiry(request: any) {
  const errMsg = `Unable to ${
    request.payload.isArchive ? 'archive' : 'un-archive'
  } enquiry`;
  try {
    const response: AxiosResponse = request.payload.isArchive
      ? yield archiveEnquiry(request.payload)
      : yield unArchiveEnquiry(request.payload);
    if (response?.data) {
      const details: AxiosResponse = yield getEnquiryDetails(request.payload);
      if (details?.data) {
        yield put(archiveEnquirySuccess(details.data));
      }
    } else {
      yield put(archiveEnquiryFailure(errMsg));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(archiveEnquiryFailure(typeof msg === 'string' ? msg : errMsg));
  }
}

function* handleUploadAttachment(request: any) {
  try {
    const payload = {
      enquiryId: request.payload.enquiryId,
      assetOwnerId: request.payload.assetOwnerId,
      file: request.payload.file,
      presentationName: request.payload.file.name
    };
    const signResponse: AxiosResponse = yield createAttachmentPresignedUrl(
      payload
    );
    if (signResponse?.data?.url && signResponse?.data?.fields) {
      const formData = new FormData();
      Object.entries(signResponse.data.fields).forEach(([key, value]) => {
        if (key === 'bucket') {
          return;
        }
        formData.append(key, String(value));
      });

      formData.append('Content-Type', request.payload.file.type);
      formData.append('file', request.payload.file);
      yield uploadFileToS3(signResponse.data.url, formData);

      const details: AxiosResponse = yield getEnquiryDetails({
        enquiryId: request.payload.enquiryId
      });
      if (details?.data) {
        yield put(getEnquiryDetailsSuccess(details.data));
      }
      yield put(uploadAttachmentSuccess(request.payload.uuid));
    } else {
      yield put(
        uploadAttachmentFailure(
          request.payload.uuid,
          'Unable to get presigned url',
          request.payload.enquiryId,
          request.payload.assetOwnerId
        )
      );
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(
      uploadAttachmentFailure(
        request.payload.uuid,
        msg || 'Unable to upload attachment',
        request.payload.enquiryId,
        request.payload.assetOwnerId
      )
    );
  }
}

function* handleDeleteAttachment(request: any) {
  try {
    const response: AxiosResponse = yield deleteAttachment(
      request.payload.id,
      request.payload.enquiryId,
      request.payload.assetOwnerId
    );
    if (response?.data) {
      const details: AxiosResponse = yield getEnquiryDetails({
        enquiryId: request.payload.enquiryId
      });
      if (details?.data) {
        yield put(getEnquiryDetailsSuccess(details.data));
      }
      yield put(deleteAttachmentSuccess(request.payload.id));
    } else {
      yield put(
        deleteAttachmentFailure(request.payload.id, 'Unable to delete file')
      );
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(
      deleteAttachmentFailure(
        request.payload.id,
        msg || 'Unable to delete file'
      )
    );
  }
}

function* handleUpdateEnquiryDetails(request: any) {
  try {
    const response: AxiosResponse = yield updateEnquiryDetails(
      request.payload.enquiryId,
      request.payload.data
    );
    if (response?.data) {
      const details: AxiosResponse = yield getEnquiryDetails(request.payload);
      if (details?.data) {
        yield put(updateEnquiryDetailsSuccess(details.data));
      }
    } else {
      yield put(updateEnquiryDetailsFailure('Unable to update enquiry'));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(updateEnquiryDetailsFailure(msg || 'Unable to update enquiry'));
  }
}

function* handleMoveEnquiryTeam(request: any) {
  const errMsg = 'Unable to move enquiry between team';
  try {
    const response: AxiosResponse = yield moveEnquiryTeam(request.payload);
    if (response) {
      yield put(moveEnquiryTeamSuccess(response));
    } else {
      yield put(moveEnquiryTeamFailure(errMsg));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(moveEnquiryTeamFailure(typeof msg === 'string' ? msg : errMsg));
  }
}

function* handleUpdateAttachment(request: any) {
  try {
    const response: AxiosResponse = yield all(
      request.payload.map((payload: any) => call(updateAttachment, payload))
    );
    if (response) {
      yield put(updateAttachmentSuccess(response));
    } else {
      yield put(updateAttachmentFailure('Unable to update attachment'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(updateAttachmentFailure(errMsg || 'Unable to update attachment'));
  }
}

function* handleGetEnquiryAutoRenewSeries(request: any) {
  try {
    const response: AxiosResponse = yield getEnquiryAutoRenewSeries(
      request.payload
    );
    if (response) {
      yield put(getEnquiryAutoRenewSeriesSuccess(response.data));
    } else {
      yield put(
        getEnquiryAutoRenewSeriesFailure(
          'Unable to get enquiry auto renew series'
        )
      );
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      getEnquiryAutoRenewSeriesFailure(
        errMsg || 'Unable to get enquiry auto renew series'
      )
    );
  }
}

function* handleAddEvent(request: any) {
  try {
    const response: AxiosResponse = yield addEvent(request.payload);
    if (response) {
      yield put(addEventSuccess(response.data));
    } else {
      yield put(addEventFailure('Unable to add event'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(addEventFailure(errMsg || 'Unable to add event'));
  }
}

function* watchGetEnquiryDetails() {
  yield takeLatest(
    DetailsPanelActionTypes.GET_ENQUIRY_DETAILS_START,
    handleGetEnquiryDetails
  );
}

function* watchGetEnquiryReferrals() {
  yield takeEvery(
    DetailsPanelActionTypes.GET_REFERRAL_DETAILS_START,
    handleGetEnquiryReferrals
  );
}

function* watchDownloadCombinedPDF() {
  yield takeLatest(
    DetailsPanelActionTypes.DOWNLOAD_COMBINED_PDF_START,
    handleDownloadCombinedPDF
  );
}

function* watchDownloadCombinedZIP() {
  yield takeLatest(
    DetailsPanelActionTypes.DOWNLOAD_COMBINED_ZIP_START,
    handleDownloadCombinedZIP
  );
}

function* watchDownloadPartialZip() {
  yield takeLatest(
    DetailsPanelActionTypes.DOWNLOAD_PARTIAL_ZIP_START,
    handleDownloadPartialZip
  );
}

function* watchViewAttachment() {
  yield takeLatest(
    DetailsPanelActionTypes.VIEW_ATTACHMENT_START,
    handleViewAttachment
  );
}

function* watchDownAttachment() {
  yield takeLatest(
    DetailsPanelActionTypes.DOWNLOAD_ATTACHMENT_START,
    handleDownAttachment
  );
}

function* watchGetEnquiryAutoRenewProperties() {
  yield takeLatest(
    DetailsPanelActionTypes.GET_ENQUIRY_AUTO_RENEW_PROPERTIES_START,
    handleGetEnquiryAutoRenewProperties
  );
}

function* watchEditEnquiryAutoRenew() {
  yield takeLatest(
    DetailsPanelActionTypes.EDIT_ENQUIRY_AUTO_RENEW_START,
    handleEditAutoRenew
  );
}

function* watchArchiveEnquiry() {
  yield takeLatest(
    DetailsPanelActionTypes.ARCHIVE_ENQUIRY_START,
    handleArchiveEnquiry
  );
}

function* watchUploadAttachment() {
  yield takeLatest(
    DetailsPanelActionTypes.UPLOAD_ATTACHMENT_START,
    handleUploadAttachment
  );
}

function* watchDeleteAttachment() {
  yield takeLatest(
    DetailsPanelActionTypes.DELETE_ATTACHMENT_START,
    handleDeleteAttachment
  );
}

function* watchUpdateEnquiryDetails() {
  yield takeLatest(
    DetailsPanelActionTypes.UPDATE_ENQUIRY_DETAILS_START,
    handleUpdateEnquiryDetails
  );
}

function* watchMoveEnquiryTeam() {
  yield takeLatest(
    DetailsPanelActionTypes.MOVE_ENQUIRY_TEAM_START,
    handleMoveEnquiryTeam
  );
}

function* watchUpdateAttachment() {
  yield takeLatest(
    DetailsPanelActionTypes.UPDATE_ATTACHMENT_START,
    handleUpdateAttachment
  );
}

function* watchGetEnquiryAutoRenewSeries() {
  yield takeLatest(
    DetailsPanelActionTypes.GET_ENQUIRY_AUTO_RENEW_SERIES_START,
    handleGetEnquiryAutoRenewSeries
  );
}

function* watchAddEvent() {
  yield takeLatest(DetailsPanelActionTypes.ADD_EVENT_START, handleAddEvent);
}

function* sagas() {
  yield all([
    call(watchGetEnquiryDetails),
    call(watchGetEnquiryReferrals),
    call(watchUpdateEnquiryDetails),
    call(watchDownloadCombinedPDF),
    call(watchDownloadCombinedZIP),
    call(watchDownloadPartialZip),
    call(watchViewAttachment),
    call(watchDownAttachment),
    call(watchGetEnquiryAutoRenewProperties),
    call(watchEditEnquiryAutoRenew),
    call(watchArchiveEnquiry),
    call(watchUploadAttachment),
    call(watchDeleteAttachment),
    call(watchMoveEnquiryTeam),
    call(watchUpdateAttachment),
    call(watchGetEnquiryAutoRenewSeries),
    call(watchAddEvent)
  ]);
}

export default sagas;
